import {
    Component,
    ViewChild,
    ElementRef,
    AfterViewChecked,
    AfterContentInit,
    OnDestroy,
    Input,
    Output,
    EventEmitter,
    ContentChildren,
    QueryList,
    TemplateRef,
    Renderer2,
    forwardRef,
    ChangeDetectorRef,
    IterableDiffers,
    ChangeDetectionStrategy,
    ViewEncapsulation
} from '@angular/core';
import { trigger, style, transition, animate, AnimationEvent } from '@angular/animations';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

import { PrimeTemplate } from 'primeng/api';
import { DomHandler, ConnectedOverlayScrollHandler } from 'primeng/dom';
import { ObjectUtils, UniqueComponentId } from 'primeng/utils';

import { Subscription } from 'rxjs';

import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ActionKeys } from '../../enums/keys.enum';
import { providePrimeNG } from 'primeng/config';

export const AUTOCOMPLETE_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PrimeAutocompleteOverrideOverlayPanelComponent),
    multi: true
};

/**
 * A modified primeng autocomplete v11.4.5
 *
 * Modified to override the overlay panel to support more customized styles to the list items.
 *
 * https://github.com/primefaces/primeng/blob/11.4.5/src/app/components/autocomplete/autocomplete.ts
 */
// TODO convert host to hostbinding or hostlistener
/* eslint-disable @angular-eslint/no-host-metadata-property */
/* eslint-disable @typescript-eslint/naming-convention */
@Component({
    selector: 'agpr-prime-autocomplete-override-overlay-panel',
    templateUrl: './prime-autocomplete-override-overlay-panel.component.html',
    animations: [
        trigger('overlayAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scaleY(0.8)' }),
                animate('{{showTransitionParams}}')
            ]),
            transition(':leave', [
                animate('{{hideTransitionParams}}', style({ opacity: 0 }))
            ])
        ])
    ],
    host: {
        '[class.p-inputwrapper-filled]': 'filled',
        '[class.p-inputwrapper-focus]': '(isFocus && !disabled) || overlayVisible'
    },
    providers: [AUTOCOMPLETE_VALUE_ACCESSOR, providePrimeNG],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./prime-autocomplete-override-overlay-panel.component.scss'],
    standalone: false
})
export class PrimeAutocompleteOverrideOverlayPanelComponent implements AfterViewChecked, AfterContentInit, OnDestroy, ControlValueAccessor {

    @Input() minLength: number = 1;
    @Input() delay: number = 300;

    @Input() style: any;
    @Input() panelStyle: any;
    @Input() styleClass: string;
    @Input() panelStyleClass: string;
    @Input() inputStyle: any;

    @Input() inputId: string;
    @Input() inputStyleClass: string;
    @Input() placeholder: string;
    @Input() readonly: boolean;
    @Input() disabled: boolean;
    @Input() virtualScroll: boolean;
    @Input() itemSize: number;
    @Input() maxlength: number;
    @Input() name: string;
    @Input() required: boolean;
    @Input() size: number;
    @Input() appendTo: any;
    @Input() autoHighlight: boolean;
    @Input() forceSelection: boolean;
    @Input() type: string = 'text';

    @Input() autoZIndex: boolean = true;
    @Input() baseZIndex: number = 0;

    @Input() ariaLabel: string = null;

    @Input() ariaLabelledBy: string = '';

    @Input() ariaDescribedBy: string = '';

    @Input() dropdownIcon: string = 'pi pi-angle-down';
    @Input() unique: boolean = true;
    @Input() group: boolean = true;
    @Input() completeOnFocus: boolean = false;

    @Input() field: string;
    @Input() scrollHeight: string = '200px';
    @Input() dropdown: boolean;
    @Input() showEmptyMessage: boolean;
    @Input() dropdownMode: string = 'blank';
    @Input() multiple: boolean;
    @Input() tabindex: number;
    @Input() dataKey: string;
    @Input() emptyMessage: string;
    @Input() showTransitionOptions: string = '.12s cubic-bezier(0, 0, 0.2, 1)';
    @Input() hideTransitionOptions: string = '.1s linear';
    @Input() autocomplete: string = 'off';
    @Input() optionGroupChildren: string;
    @Input() optionGroupLabel: string;
    @Input() resultListAriaLabel: string;
    @Input() groupTemplate: TemplateRef<any>;
    @Input() overlayTemplate: TemplateRef<any>;
    @Input() footerTemplate: TemplateRef<any>;
    @Input() itemsListTemplate: TemplateRef<any>;

    @Output() completeMethod: EventEmitter<any> = new EventEmitter();
    @Output() select: EventEmitter<any> = new EventEmitter();
    @Output() unselect: EventEmitter<any> = new EventEmitter();
    @Output() focus: EventEmitter<any> = new EventEmitter();
    @Output() blur: EventEmitter<any> = new EventEmitter();
    @Output() dropdownClick: EventEmitter<any> = new EventEmitter();
    @Output() clear: EventEmitter<any> = new EventEmitter();
    @Output() keyUp: EventEmitter<any> = new EventEmitter();
    @Output() show: EventEmitter<any> = new EventEmitter();
    @Output() hide: EventEmitter<any> = new EventEmitter();

    @Output() overlayPanelVisible: EventEmitter<boolean> = new EventEmitter();
    @Output() viewAllInGroup: EventEmitter<any> = new EventEmitter();
    @Output() researchRequest: EventEmitter<any> = new EventEmitter();

    @ViewChild('container') containerEL: ElementRef;
    @ViewChild('in') inputEL: ElementRef;
    @ViewChild('multiIn') multiInputEL: ElementRef;
    @ViewChild('multiContainer') multiContainerEL: ElementRef;
    @ViewChild('ddBtn') dropdownButton: ElementRef;
    @ViewChild('panel') panel: ElementRef;
    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;

    @ContentChildren(PrimeTemplate) templates: QueryList<any>;

    buttonTypesEnum  = ButtonTypesEnum;

    overlay: HTMLDivElement;

    itemsWrapper: HTMLDivElement;

    itemTemplate: TemplateRef<any>;

    emptyTemplate: TemplateRef<any>;

    headerTemplate: TemplateRef<any>;

    selectedItemTemplate: TemplateRef<any>;

    value: any;

    _suggestions: any[];

    timeout: any;

    overlayVisible: boolean = false;

    documentClickListener: any;

    suggestionsUpdated: boolean;

    // All list items in panel
    listElements: any;
    focusedElementIndex: number = -1;

    // Focusable children of current focused listElement
    innerFocusableElements: HTMLElement[];
    innerFocusedElementIndex: number = -1;

    isFocus: boolean = false;

    filled: boolean;

    inputClick: boolean;

    inputKeyDown: boolean;

    noResults: boolean;

    differ: any;

    inputFieldValue: string = null;

    loading: boolean;

    scrollHandler: any;

    documentResizeListener: any;

    forceSelectionUpdateModelTimeout: any;

    listId: string;

    itemClicked: boolean;

    virtualScrollSelectedIndex: number;

    subscriptions: Subscription[] = [];

    inputString: string;

    constructor(
        public el: ElementRef,
        public renderer: Renderer2,
        public cd: ChangeDetectorRef,
        public differs: IterableDiffers,
    ) {
        this.differ = differs.find([]).create(null);
        this.listId = UniqueComponentId() + '_list';
    }

    onModelChange: Function = () => {};

    onModelTouched: Function = () => {};

    @Input() get suggestions(): any[] {
        return this._suggestions;
    }

    set suggestions(val: any[]) {
        this._suggestions = val;
        this.handleSuggestionsChange();
    }

    private readonly keydownfn = (event) => {
        this.onKeydown(event);
    };

    ngAfterViewChecked() {
        // Use timeouts as since Angular 4.2, AfterViewChecked is broken and not called after panel is updated
        if (this.suggestionsUpdated && this.overlay && this.overlay.offsetParent) {
            setTimeout(() => {
                if (this.overlay) {
                    this.alignOverlay();
                }
            }, 1);
            this.suggestionsUpdated = false;
        }
    }

    handleSuggestionsChange() {
        if (this._suggestions != null && this.loading) {
            if (this._suggestions.length) {
                this.noResults = false;
                this.showOverlay();
                this.suggestionsUpdated = true;
            } else if (this.inputString) {
                this.noResults = true;
                if (this.showEmptyMessage) {
                    this.showOverlay();
                    this.suggestionsUpdated = true;
                } else {
                    this.hideOverlay();
                }
            }

            this.loading = false;
        }
    }

    ngAfterContentInit() {
        this.templates.forEach((item) => {
            switch (item.getType()) {
                case 'item':
                    this.itemTemplate = item.template;
                    break;

                case 'group':
                    this.groupTemplate = item.template;
                    break;

                case 'selectedItem':
                    this.selectedItemTemplate = item.template;
                    break;

                case 'header':
                    this.headerTemplate = item.template;
                    break;

                case 'empty':
                    this.emptyTemplate = item.template;
                    break;

                case 'footer':
                    this.footerTemplate = item.template;
                    break;

                default:
                    this.itemTemplate = item.template;
                    break;
            }
        });
    }

    updateVirtualScrollSelectedIndex() {
        if (this.focusedElementIndex > -1 && this.suggestions && this.suggestions.length) {
            this.virtualScrollSelectedIndex = this.focusedElementIndex;
        }
    }

    writeValue(value: any): void {
        this.value = value;
        this.filled = this.value && this.value !== '';
        this.updateInputField();
        this.cd.markForCheck();
    }

    getOptionGroupChildren(optionGroup: any) {
        return this.optionGroupChildren ? ObjectUtils.resolveFieldData(optionGroup, this.optionGroupChildren) : optionGroup.items;
    }

    getOptionGroupLabel(optionGroup: any) {
        return this.optionGroupLabel ?
            ObjectUtils.resolveFieldData(optionGroup, this.optionGroupLabel)
            : (optionGroup.label !== undefined ? optionGroup.label : optionGroup);
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cd.markForCheck();
    }

    onInput(event: Event) {
        // When an input element with a placeholder is clicked, the onInput event is invoked in IE.
        if (!this.inputKeyDown && DomHandler.isIE()) {
            return;
        }

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        const value = (<HTMLInputElement> event.target).value;
        if (!this.multiple && !this.forceSelection) {
            this.onModelChange(value);
        }

        if (value.length === 0 && !this.multiple) {
            this.hideOverlay();
            this.clear.emit(event);
	        this.onModelChange(value);
        }

        if (value.length >= this.minLength) {
            this.timeout = setTimeout(() => {
                this.search(event, value);
            }, this.delay);
        } else {
            this.hideOverlay();
        }
        this.updateFilledState();
        this.inputKeyDown = false;
    }

    onInputClick(event: MouseEvent) {
        if (this.documentClickListener) {
            this.inputClick = true;
        }
    }

    search(event: any, query: string) {
        // allow empty string but not undefined or null
        if (query === undefined || query === null) {
            return;
        }

        this.loading = true;
        this.completeMethod.emit({
            originalEvent: event,
            query: query
        });
    }

    selectItem = (option: any, focus: boolean = true) => {
        if (this.forceSelectionUpdateModelTimeout) {
            clearTimeout(this.forceSelectionUpdateModelTimeout);
            this.forceSelectionUpdateModelTimeout = null;
        }

        if (this.multiple) {
            this.multiInputEL.nativeElement.value = '';
            this.value = this.value || [];
            if (!this.isSelected(option) || !this.unique) {
                this.value = [...this.value, option];
                this.onModelChange(this.value);
            }
        } else {
            this.inputEL.nativeElement.value =  this.resolveFieldData(option);
            this.value = option;
            this.onModelChange(this.value);
        }

        this.select.emit(option);
        this.updateFilledState();

        if (focus) {
            this.itemClicked = true;
            this.focusInput();
        }
    };

    showOverlay() {
        if (this.multiInputEL || this.inputEL) {
            const hasFocus = this.multiple ?
                this.multiInputEL.nativeElement.ownerDocument.activeElement === this.multiInputEL.nativeElement :
                this.inputEL.nativeElement.ownerDocument.activeElement === this.inputEL.nativeElement;

            if (!this.overlayVisible && hasFocus) {
                this.overlayVisible = true;
            }
        }
    }

    onOverlayAnimationStart(event: AnimationEvent) {
        switch (event.toState) {
            case 'visible':
                this.overlay = event.element;
                this.itemsWrapper = this.virtualScroll ? DomHandler.findSingle(this.overlay, '.cdk-virtual-scroll-viewport') : this.overlay;
                this.appendOverlay();
                if (this.autoZIndex) {
                    this.overlay.style.zIndex = String(this.baseZIndex + (++DomHandler.zindex));
                }
                this.alignOverlay();
                this.bindDocumentClickListener();
                this.bindDocumentResizeListener();
                this.bindScrollListener();
                this.show.emit(event);
                break;

            case 'void':
                this.onOverlayHide();
                break;
        }
    }

    getListElements() {
        if (this.listElements?.length > 0) {
            this.listElements.forEach(element => {
                element.removeEventListener('keydown', this.keydownfn);
            });
        }
        this.listElements = this.panel.nativeElement.querySelectorAll('li');
        this.listElements.forEach(element => {
            element.addEventListener('keydown', this.keydownfn);
            if (element.querySelectorAll('button, [href], input, select, textarea, [tabindex]').length === 0) {
                element.setAttribute('tabindex', 0);
            }
        });
        this.focusedElementIndex = -1;
        this.innerFocusableElements = [];
        this.innerFocusedElementIndex = -1;
    }

    appendOverlay() {
        if (this.appendTo) {
            if (this.appendTo === 'body') {
                document.body.appendChild(this.overlay);
            } else {
                DomHandler.appendChild(this.overlay, this.appendTo);
            }
            if (!this.overlay.style.minWidth) {
                this.overlay.style.minWidth = DomHandler.getWidth(this.el.nativeElement.children[0]) + 'px';
            }
        }
    }

    resolveFieldData(value) {
        const data = this.field ? ObjectUtils.resolveFieldData(value, this.field) : value;
        return data !== null && data !== undefined ? data : '';
    }

    restoreOverlayAppend() {
        if (this.overlay && this.appendTo) {
            this.el.nativeElement.appendChild(this.overlay);
        }
    }

    alignOverlay() {
        if (this.appendTo) {
            DomHandler.absolutePosition(this.overlay, (this.multiple ? this.multiContainerEL.nativeElement : this.inputEL.nativeElement));
        } else {
            DomHandler.relativePosition(this.overlay, (this.multiple ? this.multiContainerEL.nativeElement : this.inputEL.nativeElement));
        }
    }

    hideOverlay() {
        this.overlayVisible = false;
        this.focusedElementIndex = -1;
        this.innerFocusableElements = [];
        this.innerFocusedElementIndex = -1;
        this.cd.markForCheck();
    }

    handleDropdownClick(event) {
        if (!this.overlayVisible) {
            this.focusInput();
            const queryValue = this.multiple ? this.multiInputEL.nativeElement.value : this.inputEL.nativeElement.value;

            if (this.dropdownMode === 'blank') {
                this.search(event, '');
            } else if (this.dropdownMode === 'current') {
                this.search(event, queryValue);
            }

            this.dropdownClick.emit({
                originalEvent: event,
                query: queryValue
            });
        } else {
            this.hideOverlay();
        }
    }

    focusInput() {
        if (this.multiple) {
            this.multiInputEL.nativeElement.focus();
        } else {
            this.inputEL.nativeElement.focus();
        }
    }

    get emptyMessageLabel(): string {
        // @NOTE can uncomment if we can use primeng version >= 11.4.5
        // return this.emptyMessage || this.config.getTranslation(TranslationKeys.EMPTY_MESSAGE);
        return this.emptyMessage;
    }

    removeItem(item: any) {
        const itemIndex = DomHandler.index(item);
        const removedValue = this.value[itemIndex];
        this.value = this.value.filter((val, i) => i !== itemIndex);
        this.onModelChange(this.value);
        this.updateFilledState();
        this.unselect.emit(removedValue);
    }

    onKeydown(event) {
        if (this.overlayVisible) {
            if (this.focusedElementIndex === -1) {
                this.getListElements();
            }
            switch (event.key) {
                case ActionKeys.DOWN:
                    if (this.innerFocusedElementIndex > -1) {
                        if (this.innerFocusedElementIndex < this.innerFocusableElements?.length - 1) {
                            this.innerFocusedElementIndex++;
                            this.innerFocusableElements[this.innerFocusedElementIndex].focus();
                        } else {
                            this.innerFocusableElements = [];
                            this.innerFocusedElementIndex = -1;
                        }
                    }
                    if (this.innerFocusedElementIndex === -1 && this.focusedElementIndex < this.listElements.length - 1) {
                        this.focusedElementIndex++;
                        if (this.listElements[this.focusedElementIndex].classList.contains('group')
                            || this.listElements[this.focusedElementIndex].classList.contains('footer')) {

                            this.innerFocusableElements = Array.from(this.listElements[this.focusedElementIndex]
                                .querySelectorAll('button, [href], input, select, textarea, [tabindex]'));

                            if (this.innerFocusableElements.length > 0) {
                                this.innerFocusedElementIndex = 0;
                                this.innerFocusableElements[this.innerFocusedElementIndex].focus();
                            } else {
                                this.focusedElementIndex++;
                                this.listElements[this.focusedElementIndex].focus();
                            }
                        } else {
                            this.listElements[this.focusedElementIndex].focus();
                        }
                    }
                    event.preventDefault();
                    break;

                case ActionKeys.UP:
                    if (this.innerFocusedElementIndex > -1) {
                        if (this.innerFocusedElementIndex > 0) {
                            this.innerFocusedElementIndex--;
                            this.innerFocusableElements[this.innerFocusedElementIndex].focus();
                        } else {
                            this.innerFocusableElements = [];
                            this.innerFocusedElementIndex = -1;
                        }
                    }
                    if (this.innerFocusedElementIndex === -1 && this.focusedElementIndex > 0) {
                        this.focusedElementIndex--;
                        if (this.listElements[this.focusedElementIndex].classList.contains('group')
                            || this.listElements[this.focusedElementIndex].classList.contains('footer')) {

                            this.innerFocusableElements = this.listElements[this.focusedElementIndex]
                                .querySelectorAll('button, [href], input, select, textarea, [tabindex]');

                            if (this.innerFocusableElements) {
                                this.innerFocusedElementIndex = this.innerFocusableElements.length - 1;
                                this.innerFocusableElements[this.innerFocusedElementIndex].focus();
                            } else {
                                this.focusedElementIndex--;
                                this.listElements[this.focusedElementIndex].focus();
                            }
                        } else {
                            this.listElements[this.focusedElementIndex].focus();
                        }
                    } else if (this.focusedElementIndex === 0) {
                        this.inputEL.nativeElement.focus();
                        this.hideOverlay();
                    }
                    event.preventDefault();
                    break;

                case ActionKeys.ENTER:
                case ActionKeys.SPACE:
                    if (this.focusedElementIndex > -1 && document.activeElement === this.listElements[this.focusedElementIndex]) {
                        this.listElements[this.focusedElementIndex].click();
                    }
                    break;

                case ActionKeys.ESCAPE:
                    this.inputEL.nativeElement.focus();
                    this.hideOverlay();
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    break;

                case ActionKeys.TAB:
                    this.inputEL.nativeElement.focus();
                    event.preventDefault();
                    this.hideOverlay();
                    break;
            }
        } else {
            if ((event.key === ActionKeys.ENTER || event.key === ActionKeys.DOWN) && this.suggestions) {
                this.search(event, event.target.value);
            }
        }

        if (this.multiple) {
            switch (event.key) {
                case ActionKeys.BACKSPACE:
                    if (this.value && this.value.length && !this.multiInputEL.nativeElement.value) {
                        this.value = [...this.value];
                        const removedValue = this.value.pop();
                        this.onModelChange(this.value);
                        this.updateFilledState();
                        this.unselect.emit(removedValue);
                    }
                    break;
            }
        }

        this.inputKeyDown = true;
    }

    onKeyup(event) {
        this.keyUp.emit(event);
    }

    onInputFocus(event) {
        if (!this.itemClicked && this.completeOnFocus ) {
            const queryValue = this.multiple ? this.multiInputEL.nativeElement.value : this.inputEL.nativeElement.value;
            this.search(event, queryValue);
        }

        this.isFocus = true;
        this.focus.emit(event);
        this.itemClicked = false;
    }

    onInputBlur(event) {
        this.isFocus = false;
        this.onModelTouched();
        this.blur.emit(event);
    }

    onInputChange(event) {
        if (this.forceSelection) {
            let valid = false;
            const inputValue = event.target.value.trim();

            if (this.suggestions)  {
                for (const suggestion of this.suggestions) {
                    const itemValue = this.field ? ObjectUtils.resolveFieldData(suggestion, this.field) : suggestion;
                    if (itemValue && inputValue === itemValue.trim()) {
                        valid = true;
                        this.forceSelectionUpdateModelTimeout = setTimeout(() => {
                            this.selectItem(suggestion, false);
                        }, 250);
                        break;
                    }
                }
            }

            if (!valid) {
                if (this.multiple) {
                    this.multiInputEL.nativeElement.value = '';
                } else {
                    this.value = null;
                    this.inputEL.nativeElement.value = '';
                }

                this.clear.emit(event);
                this.onModelChange(this.value);
                this.updateFilledState();
            }
        }
    }

    onInputPaste(event: ClipboardEvent) {
        this.onKeydown(event);
    }

    isSelected(val: any): boolean {
        let selected: boolean = false;
        if (this.value && this.value.length) {
            for (let i = 0; i < this.value.length; i++) {
                if (ObjectUtils.equals(this.value[i], val, this.dataKey)) {
                    selected = true;
                    break;
                }
            }
        }
        return selected;
    }

    findOptionIndex(option, suggestions): number {
        let index: number = -1;
        if (suggestions) {
            for (let i = 0; i < suggestions.length; i++) {
                if (ObjectUtils.equals(option, suggestions[i])) {
                    index = i;
                    break;
                }
            }
        }

        return index;
    }

    findOptionGroupIndex(val: any, opts: any[]): any {
        let groupIndex, itemIndex;

        if (opts) {
            for (let i = 0; i < opts.length; i++) {
                groupIndex = i;
                itemIndex = this.findOptionIndex(val, this.getOptionGroupChildren(opts[i]));

                if (itemIndex !== -1) {
                    break;
                }
            }
        }

        if (itemIndex !== -1) {
            return {groupIndex: groupIndex, itemIndex: itemIndex};
        } else {
            return -1;
        }
    }

    updateFilledState() {
        if (this.multiple) {
            this.filled = (this.value && this.value.length)
                || (this.multiInputEL && this.multiInputEL.nativeElement && this.multiInputEL.nativeElement.value !== '');
        } else {
            this.filled = (this.inputFieldValue && this.inputFieldValue !== '')
                || (this.inputEL && this.inputEL.nativeElement && this.inputEL.nativeElement.value !== '');
        }
    }

    updateInputField() {
        const formattedValue = this.resolveFieldData(this.value);
        this.inputFieldValue = formattedValue;

        if (this.inputEL && this.inputEL.nativeElement) {
            this.inputEL.nativeElement.value = formattedValue;
        }

        this.updateFilledState();
    }

    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

            this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
                if (event.which === 3) {
                    return;
                }

                if (!this.inputClick && !this.isDropdownClick(event)) {
                    this.hideOverlay();
                }

                this.inputClick = false;
                this.cd.markForCheck();
            });
        }
    }

    isDropdownClick(event) {
        if (this.dropdown) {
            const target = event.target;
            return (target === this.dropdownButton.nativeElement || target.parentNode === this.dropdownButton.nativeElement);
        } else {
            return false;
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    bindDocumentResizeListener() {
        this.documentResizeListener = this.onWindowResize.bind(this);
        window.addEventListener('resize', this.documentResizeListener);
    }

    unbindDocumentResizeListener() {
        if (this.documentResizeListener) {
            window.removeEventListener('resize', this.documentResizeListener);
            this.documentResizeListener = null;
        }
    }

    onWindowResize() {
        this.hideOverlay();
    }

    bindScrollListener() {
        if (!this.scrollHandler) {
            this.scrollHandler = new ConnectedOverlayScrollHandler(this.containerEL.nativeElement, () => {
                if (this.overlayVisible) {
                    this.hideOverlay();
                }
            });
        }

        this.scrollHandler.bindScrollListener();
    }

    unbindScrollListener() {
        if (this.scrollHandler) {
            this.scrollHandler.unbindScrollListener();
        }
    }

    onOverlayHide() {
        this.unbindDocumentClickListener();
        this.unbindDocumentResizeListener();
        this.unbindScrollListener();
        this.overlay = null;
        this.hide.emit();
    }

    ngOnDestroy() {
        if (this.forceSelectionUpdateModelTimeout) {
            clearTimeout(this.forceSelectionUpdateModelTimeout);
            this.forceSelectionUpdateModelTimeout = null;
        }

        if (this.scrollHandler) {
            this.scrollHandler.destroy();
            this.scrollHandler = null;
        }
        this.restoreOverlayAppend();
        this.onOverlayHide();
    }

    triggerViewAllInGroup = (group: any) => {
        this.viewAllInGroup.emit(group);
    };

    triggerResearchRequest = () => {
        this.researchRequest.emit(null);
    };
}
